import {useContext, useEffect, useMemo, useState} from "react";
import {useTranslation} from "react-i18next";
import toast from "react-hot-toast";
import {useLocation, useNavigate} from "react-router-dom";
import Banner2 from "../components/Banner2.jsx";
import Button from "../components/Button.jsx";
import LoadingComponent from "../components/LoadingComponent.jsx";
import useDownloadState from "../api/utilities/useDownloadState.js";
import {GlobalContext} from "../utils/context/GlobalContextProvider.jsx";
import {AuthenticationApi, ProjectIntegrationsApi} from "../api/authority/generated/index";
import {useErrorTranslation} from "../api/utilities/translateErrorMessage.js";

export default function AppAlerts({alerts = [], className = ''}) {
    const {t} = useTranslation();
    const {et} = useErrorTranslation();
    const {context, getAuthorityConfiguration} = useContext(GlobalContext);
    const location = useLocation();
    const navigate = useNavigate();
    const [state, setDownloading] = useDownloadState();
    const [buttonPressed, setButtonPressed] = useState(false);
    const [projectIntegrations, setProjectIntegrations] = useState(null);

    const currentProject = useMemo(() => {
        return context.currentUserProjects?.find((project) => project.id === context.currentProjectId) ?? null;
    }, [context.currentProjectId, context.currentUserProjects]);

    useEffect(() => {
        if (!buttonPressed) {
            return;
        }

        setButtonPressed(false);
        setDownloading(true);

        new AuthenticationApi(getAuthorityConfiguration())
            .resendConfirmationLink({
                resendConfirmEmailInput: {
                    email: context.currentIdentity?.user?.email ?? '',
                },
            })
            .then(() => {
                toast.success(t('user.confirmation-email-sent'));
            })
            .catch((e) => et(e).then(toast.error))
            .finally(() => setDownloading(false));
    }, [buttonPressed, context.currentIdentity?.user?.email, et, getAuthorityConfiguration, setDownloading, t]);

    useEffect(() => {
        if (!currentProject?.id || currentProject.hasErpIntegration !== true) {
            setProjectIntegrations(null);
            return;
        }

        let cancelled = false;

        new ProjectIntegrationsApi(getAuthorityConfiguration())
            .listProjectIntegrations({projectId: currentProject.id})
            .then((result) => {
                if (!cancelled) {
                    setProjectIntegrations(result);
                }
            })
            .catch(() => {
                if (!cancelled) {
                    setProjectIntegrations([]);
                }
            });

        return () => {
            cancelled = true;
        };
    }, [currentProject?.hasErpIntegration, currentProject?.id, getAuthorityConfiguration]);

    const addonBootstrapAlert = useMemo(() => {
        if (!currentProject?.id || !Array.isArray(projectIntegrations)) {
            return null;
        }

        const hasAddonWaitingForFirstSync = projectIntegrations.some((integration) => {
            return integration?.name === 'UpgatesAddon' && !integration?.lastRegularUpdate;
        });

        if (!hasAddonWaitingForFirstSync) {
            return null;
        }

        const integrationsPath = `/project/${currentProject.id}/settings/integrations`;

        return {
            id: 'addon-bootstrap-first-sync',
            type: 'info',
            showClose: false,
            hiddenOnPaths: [integrationsPath],
            content: (
                <span>
                    {t('app-alerts.addon-bootstrap-pending.message')}
                </span>
            ),
            action: (
                <Button
                    variant="outline-primary"
                    onClick={() => navigate(integrationsPath)}
                >
                    {t('app-alerts.addon-bootstrap-pending.action')}
                </Button>
            ),
        };
    }, [currentProject?.id, navigate, projectIntegrations, t]);

    const globalAlerts = useMemo(() => {
        const configuredGlobalAlerts = context.globalAlerts ?? [];
        const dynamicGlobalAlerts = [addonBootstrapAlert].filter(Boolean);

        if (context.currentIdentity === null || context.currentIdentity.user?.emailConfirmed === true) {
            return [...dynamicGlobalAlerts, ...configuredGlobalAlerts];
        }

        return [
            {
                id: 'confirm-email',
                type: 'warning',
                showClose: false,
                content: t('user.email-not-confirmed'),
                action: (
                    <LoadingComponent loading={state.downloading}>
                        <Button variant="outline-warning" onClick={() => setButtonPressed(true)}>
                            {t('user.resend-confirmation-email')}
                        </Button>
                    </LoadingComponent>
                ),
            },
            ...dynamicGlobalAlerts,
            ...configuredGlobalAlerts,
        ];
    }, [addonBootstrapAlert, context.currentIdentity, context.globalAlerts, state.downloading, t]);

    const allAlerts = [...globalAlerts, ...alerts].filter((alert) => {
        if (!alert) {
            return false;
        }

        const hiddenOnPaths = alert.hiddenOnPaths ?? [];

        return !hiddenOnPaths.some((pathRule) => {
            if (typeof pathRule === 'function') {
                return pathRule(location.pathname);
            }

            if (pathRule instanceof RegExp) {
                return pathRule.test(location.pathname);
            }

            if (typeof pathRule === 'string') {
                return location.pathname === pathRule || location.pathname.startsWith(`${pathRule}/`);
            }

            return false;
        });
    });

    if (allAlerts.length === 0) {
        return null;
    }

    return (
        <div className={`flex-0 pb-8 pt-2 flex flex-col gap-2 ${className}`.trim()}>
            {allAlerts.map((alert) => (
                <div key={alert.id} className={'alert-wrap'}>
                    <Banner2
                        type={alert.type ?? 'info'}
                        open={alert.open ?? true}
                        setOpen={alert.setOpen ?? (() => null)}
                        showClose={alert.showClose ?? false}
                        className={alert.className}
                    >
                        <div className="flex w-full flex-col gap-3 sm:flex-row sm:items-center">
                            <div className="min-w-0 flex-1 leading-6">{alert.content}</div>
                            {alert.action ? <div className="flex shrink-0 justify-end sm:ml-auto">{alert.action}</div> : null}
                        </div>
                    </Banner2>
                </div>
            ))}
        </div>
    );
}
